/**
 * Cart hooks — read, add line, update line, delete line, delete item.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import { withQuoteCheckoutScope } from "@/lib/quoteCheckout";
import type {
  AssemblyIntent,
  AddLineRequest,
  AssemblyChildRequest,
  AssemblyComposeResponse,
  AssemblyPreview,
  Cart,
  CartAddons,
  CartLine,
  CartRfqRequest,
  CartRfqResponse,
  CartSplitPreview,
  CartSplitResponse,
  CheckoutPickup,
  CheckoutTotalsPreview,
} from "@/api/types";

import { queryKeys } from "./keys";
import { chainContinuationKeys } from "./chainContinuationKeys";

const API = "/api/v1";

/**
 * KAN-2093 — zneplatni košík a VŠECHNO, co se počítá z jeho obsahu.
 *
 * Nabídka dopravců a „vejde se do dopravy" jsou spočítané z hmotnosti
 * košíku, ale klíče mají pod ``["checkout"]``, takže je zneplatnění
 * ``["cart"]`` minulo: po přidání 102kg zakázky košík i pokladna dál
 * nabízely dopravu spočítanou pro lehký košík z minula.
 */
export function invalidateCart(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: queryKeys.cart() });
  qc.invalidateQueries({ queryKey: ["checkout", "carriers"] });
  qc.invalidateQueries({ queryKey: ["checkout", "shippingAvailability"] });
}

// --- Cart -------------------------------------------------------------

export function useCart(
  options?: Omit<UseQueryOptions<Cart>, "queryKey" | "queryFn">,
) {
  return useQuery<Cart>({
    // KAN-1658 — v pokladně nabídky jsou „košíkem" položky nabídky.
    queryKey: withQuoteCheckoutScope(queryKeys.cart()),
    queryFn: () => apiFetch<Cart>(`${API}/cart`),
    staleTime: 5 * 1000,
    ...options,
  });
}

// --- Per-capacity delivery options for the current cart ---------------
//
// Ladder-keyed delivery options (PR4 BE shape). One option per active
// canonical rung (``standard`` / ``fast`` / ``express``). Empty when
// the tenant hasn't defined ladder rungs yet — checkout falls back
// to the legacy ``catalog.delivery_options`` dropdown. ``fallbacks``
// carries the per-service "this rung wasn't directly available"
// warnings the FE renders as inline notes under the radio row.

export interface CartCapacityChoice {
  tier_id: number;
  process_type: string;
  code: string;
  label_cz: string;
  /** Optional English override (null/absent ⇒ fall back to ``label_cz``). */
  label_en?: string | null;
  /** Popisek per jazyk platformy (KAN-1804); neprázdný blob vyhrává nad ``label_cz`` / ``label_en``. */
  label_i18n?: Record<string, string>;
  sort_order: number;
  multiplier: number;
  flat_surcharge_czk: number;
  base_min_days: number;
  items_per_day: number;
  day_basis: "calendar" | "business";
}

export interface CartLadderFallback {
  process_type: string;
  requested_ladder_code: string;
  actual_ladder_code: string;
}

export interface CartDeliveryLadderOption {
  ladder_id: number;
  ladder_code: string;
  ladder_label_cz: string;
  /** Optional English override (null/absent ⇒ fall back to ``ladder_label_cz``). */
  ladder_label_en?: string | null;
  ladder_label_i18n?: Record<string, string>;
  ladder_sort_order: number;
  days_calendar: number;
  days_business: number | null;
  day_basis: "calendar" | "business" | "mixed";
  flat_surcharge_czk: number;
  multiplier_surcharge_czk: number;
  per_capacity: CartCapacityChoice[];
  fallbacks: CartLadderFallback[];
  /** KAN-925 — production finish date (ISO ``YYYY-MM-DD``) from the one
   * backend estimate the order ETA uses too. The FE only adds the
   * chosen carrier's transit days on top. */
  estimated_delivery_date: string | null;
  /** KAN-1438 — některý krok v košíku nemá odhad doby; po platbě by
   * termín zněl „Termín upřesníme“, takže datum je ``null`` a pokladna
   * i košík ukážou totéž místo data. */
  eta_pending_estimate?: boolean;
  /** KAN-1901 — proč datum chybí: ``material_lead_time`` = chybí materiál,
   * o jehož dodání obchod nic neví (rychlé úrovně se pak nenabízejí);
   * ``duration_estimate`` = krok bez odhadu doby (KAN-1438). */
  eta_pending_reason?: "duration_estimate" | "material_lead_time" | null;
}

export function useCartDeliveryLadderOptions(
  options?: Omit<
    UseQueryOptions<CartDeliveryLadderOption[]>,
    "queryKey" | "queryFn"
  >,
  /** KAN-1623 — dny přepravy zvoleného dopravce. Server je započítá do
   *  ``estimated_delivery_date`` (v pracovních dnech, jako ETA objednávky),
   *  takže datum je rovnou datum DORUČENÍ. 0 = dokončení výroby. */
  transitDays = 0,
  /** KAN-1643 — země doručení: přeprava přeskočí její státní svátky. */
  destinationCountry?: string,
) {
  const params = new URLSearchParams();
  if (transitDays > 0) params.set("transit_days", String(transitDays));
  if (transitDays > 0 && destinationCountry)
    params.set("destination_country", destinationCountry);
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  return useQuery<CartDeliveryLadderOption[]>({
    queryKey: withQuoteCheckoutScope([
      "cart",
      "deliveryOptions",
      "ladders",
      transitDays,
      transitDays > 0 ? (destinationCountry ?? null) : null,
    ]),
    queryFn: () =>
      apiFetch<CartDeliveryLadderOption[]>(
        `${API}/cart/delivery-options/ladders${qs}`,
      ),
    staleTime: 30 * 1000,
    ...options,
  });
}

// --- Authoritative checkout totals (KAN-1514) -------------------------
//
// The last screen before payment used to add its own rows up, and the
// top-up it took from ``GET /cart`` was computed without shipping — so it
// showed 619 where the order said 500. This endpoint answers with the
// breakdown the engine will stamp; the screen only renders it.
//
// Every parameter is optional because the summary is drawn while the
// customer has not picked yet: a partial breakdown beats an empty one.

export function useCheckoutTotalsPreview(
  params: {
    countryCode?: string;
    carrierCode?: string;
    ladderId?: number | null;
    /** KAN-1605 — ``"pickup"`` ocení osobní odběr místo dopravce. */
    deliveryMethod?: "carrier" | "pickup";
    /** KAN-1656 — DIČ kupujícího (už ve správném formátu): ověřené DIČ
     *  z jiné země EU znamená přenesení daňové povinnosti, a souhrn musí
     *  ukázat totéž, co pak pokladna vybere. */
    vatId?: string;
  },
  options?: Omit<
    UseQueryOptions<CheckoutTotalsPreview | null>,
    "queryKey" | "queryFn"
  >,
) {
  const {
    countryCode,
    carrierCode,
    ladderId = null,
    deliveryMethod = "carrier",
    vatId,
  } = params;
  const search = new URLSearchParams();
  if (countryCode) search.set("country_code", countryCode);
  if (deliveryMethod === "pickup") search.set("delivery_method", "pickup");
  else if (carrierCode) search.set("carrier_code", carrierCode);
  if (ladderId != null) search.set("ladder_id", String(ladderId));
  if (vatId) search.set("dic", vatId);
  const qs = search.toString();
  return useQuery<CheckoutTotalsPreview | null>({
    queryKey: withQuoteCheckoutScope(
      queryKeys.cartCheckoutPreview(
        countryCode,
        carrierCode,
        ladderId,
        deliveryMethod,
        vatId,
      ),
    ),
    queryFn: () =>
      apiFetch<CheckoutTotalsPreview | null>(
        `${API}/cart/checkout-preview${qs ? `?${qs}` : ""}`,
      ),
    ...options,
  });
}

// --- Active shipping carriers for the current tenant ------------------
//
// Backs the checkout step-2 "Shipping method" dropdown. Replaces the
// previously-hardcoded zasilkovna/dpd/ppl/pickup options so each
// tenant sees their own admin-configured carriers.
//
// Pass ``countryCode`` once the customer has filled in step 1 — the BE
// then prices each carrier for the cart's weight × country and omits
// any carrier that can't deliver. Without ``countryCode`` the BE returns
// the unpriced list (legacy behavior) so the dropdown still renders
// before step 1 is complete.

export interface CheckoutCarrier {
  code: string;
  name: string;
  /** Optional English override; NULL/absent ⇒ fall back to ``name``. */
  name_en?: string | null;
  /** Název per jazyk platformy (KAN-1804); neprázdný blob vyhrává nad ``name`` / ``name_en``. */
  name_i18n?: Record<string, string>;
  price_czk: number | null;
  /** Total "počet dní navíc pro dopravu" for this carrier × country ×
   * matched weight band (carrier + country + rate). NULL when the cart
   * isn't quotable yet — same rule as ``price_czk``. */
  extra_days?: number | null;
}

export function useCheckoutCarriers(
  countryCode?: string,
  options?: Omit<UseQueryOptions<CheckoutCarrier[]>, "queryKey" | "queryFn">,
) {
  const country = countryCode?.trim() || undefined;
  return useQuery<CheckoutCarrier[]>({
    queryKey: withQuoteCheckoutScope(["checkout", "carriers", country ?? null]),
    queryFn: () =>
      apiFetch<CheckoutCarrier[]>(
        country
          ? `${API}/checkout/carriers?country_code=${encodeURIComponent(country)}`
          : `${API}/checkout/carriers`,
      ),
    staleTime: 30 * 1000,
    ...options,
  });
}

// --- Does the cart fit into any shipping band? (KAN-1754) -----------------
//
// ``fits_nowhere`` = the shop ships to the country, but no band takes every
// part (``shipping_rates.fit_spec``, KAN-484). ``/checkout/carriers`` then
// answers an empty list and the checkout offers a price enquiry instead.

export interface CheckoutShippingAvailability {
  fits_nowhere: boolean;
}

export function useCheckoutShippingAvailability(countryCode?: string) {
  const country = countryCode?.trim() || undefined;
  return useQuery<CheckoutShippingAvailability>({
    queryKey: withQuoteCheckoutScope(["checkout", "shippingAvailability", country ?? null]),
    queryFn: () =>
      apiFetch<CheckoutShippingAvailability>(
        `${API}/checkout/shipping-availability?country_code=${encodeURIComponent(country ?? "")}`,
      ),
    enabled: country !== undefined,
    staleTime: 30 * 1000,
  });
}

// --- Osobní odběr obchodu (KAN-1605) -------------------------------------
//
// Samostatný způsob doručení, ne dopravce: pokladna ho nabízí jako POSLEDNÍ
// volbu za dopravci. ``null`` = obchod odběr nenabízí.

export function useCheckoutPickup(
  options?: Omit<
    UseQueryOptions<CheckoutPickup | null>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<CheckoutPickup | null>({
    queryKey: ["checkout", "pickup"],
    queryFn: () => apiFetch<CheckoutPickup | null>(`${API}/checkout/pickup`),
    staleTime: 30 * 1000,
    ...options,
  });
}

// --- "Doprava od X" estimate for the cart summary (KAN-483) -----------
//
// The cart has no delivery address, so it cannot price shipping the
// way the checkout does. This endpoint answers with the cheapest
// carrier + shortest transit for the tenant's DEFAULT delivery country
// (first row of the admin-ordered list), computed through the same
// backend path as ``/checkout/carriers`` so the "od" is never a number
// the checkout cannot produce.
//
// Every field is nullable and the endpoint always answers 200: a
// not-yet-quotable cart or a tenant with no countries/rates yields
// nulls, and the summary says shipping is calculated at checkout
// instead of showing a figure.

export interface CartShippingEstimate {
  /** The country the figures were priced for; null ⇒ nothing to show. */
  country_code: string | null;
  /** Cheapest carrier for that country, in MINOR units of
   * ``currency_code`` (KAN-284 — the amount field does not name the
   * currency). Convert with ``minorToMajor`` before formatting. */
  price_minor: number | null;
  currency_code: string | null;
  /** Transit days of the FASTEST carrier — minimised independently of
   * ``price_minor``, so the two may describe different carriers. Both
   * hold as lower bounds, which is all "od" claims. */
  extra_days: number | null;
  /** KAN-1754 — the cart fits into no shipping band to ``country_code``;
   * the summary offers a price enquiry instead of a "from" price. */
  fits_nowhere?: boolean;
}

export function useCartShippingEstimate(
  options?: Omit<UseQueryOptions<CartShippingEstimate>, "queryKey" | "queryFn">,
) {
  return useQuery<CartShippingEstimate>({
    queryKey: ["cart", "shippingEstimate"],
    queryFn: () =>
      apiFetch<CartShippingEstimate>(`${API}/checkout/shipping-estimate`),
    staleTime: 30 * 1000,
    ...options,
  });
}

interface AddCartLineVars {
  // Opaque quote slug. Server rejects numeric ids now (they never
  // match the slug column), so the field is strongly string-typed to
  // catch any caller still passing ``Number(...)``.
  itemSlug: string;
  line: AddLineRequest;
}

export function useAddCartLine(
  options?: Omit<
    UseMutationOptions<CartLine, Error, AddCartLineVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<CartLine, Error, AddCartLineVars>({
    mutationFn: ({ itemSlug, line }) =>
      apiFetch<CartLine>(`${API}/cart/items/${itemSlug}/lines`, {
        method: "POST",
        body: line,
      }),
    onSuccess: () => {
      invalidateCart(qc);
    },
    ...options,
  });
}

interface UpdateCartLineRequest {
  /** Optional — the PATCH is a TRUE PARTIAL MERGE server-side; absent
   *  fields keep the stored values. */
  material_code?: string;
  quantity: number;
  /**
   * Universal per-process variant dimensions — the canonical channel.
   * Each process sends its own declared keys (``{<axis>: value}``; 3D e.g. ``{<axis>:
   * 0.2, …}``; paint sends its own declared keys.
   * The backend merges payload dimensions over the stored ones, so
   * absent keys keep their previous values.
   */
  dimensions?: Record<string, unknown>;
  /**
   * Free-form per-process knobs that don't fit the typed fields above
   * — each process its declared set,
   * future processes can carry their own keys. The backend merges this
   * blob into ``config_snapshot.extra_config`` so cart-side inline
   * edits round-trip through ``get_cart`` correctly.
   */
  extra_config?: Record<string, unknown>;
  /**
   * Customer-typed note for this variant. ``null`` clears the existing
   * note; omitting the field keeps the previous value. Limited to 1024
   * chars (BE validated).
   */
  customer_note?: string | null;
}

interface UpdateCartLineVars {
  lineId: number;
  payload: UpdateCartLineRequest;
}

export function useUpdateCartLine(
  options?: Omit<
    UseMutationOptions<CartLine, Error, UpdateCartLineVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<CartLine, Error, UpdateCartLineVars>({
    mutationFn: ({ lineId, payload }) =>
      apiFetch<CartLine>(`${API}/cart/lines/${lineId}`, {
        method: "PATCH",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // KAN-1876 — úprava přecenila řádek změněným ceníkem: košík to
      // zákazníkovi řekne (``PriceListChangeNotice``). Běžná změna pásma
      // nic nenese a starou hlášku smaže.
      qc.setQueryData(
        queryKeys.cartPriceListChange(),
        data?.price_list_change
          ? { lineId: variables.lineId, ...data.price_list_change }
          : null,
      );
      invalidateCart(qc);
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

export function useDeleteCartLine(
  options?: Omit<UseMutationOptions<void, Error, number>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (lineId) =>
      apiFetch<void>(`${API}/cart/lines/${lineId}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidateCart(qc);
    },
    ...options,
  });
}

// --- Per-line ladder override (PR6 BE) -------------------------------
//
// PATCH /cart/lines/{lineId}/ladder lets a power user pin one cart
// line to a different ladder rung than the cart-wide pick. NULL =
// clear the override. Returns 422 ``ladder_not_available_for_capacity``
// when the picked rung has no active capacity tier for the line's
// primary process_type (FE picker should already disable such picks
// before submit).

export interface CartLineLadderOverrideResponse {
  line_id: number;
  ladder_tier_id: number | null;
}

interface SetLineLadderVars {
  lineId: number;
  ladderTierId: number | null;
}

export function useSetCartLineLadder(
  options?: Omit<
    UseMutationOptions<
      CartLineLadderOverrideResponse,
      Error,
      SetLineLadderVars
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    CartLineLadderOverrideResponse,
    Error,
    SetLineLadderVars
  >({
    mutationFn: ({ lineId, ladderTierId }) =>
      apiFetch<CartLineLadderOverrideResponse>(
        `${API}/cart/lines/${lineId}/ladder`,
        {
          method: "PATCH",
          body: { ladder_tier_id: ladderTierId },
        },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // Refetch the cart so prices reflect the new multiplier and
      // surface a fresh delivery-options list (the override changes
      // the bottleneck math).
      invalidateCart(qc);
      qc.invalidateQueries({ queryKey: ["cart", "deliveryOptions"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Customer-typed order note (cart summary textarea) ---------------
//
// Live auto-save from ``CartSummaryV2`` — the textarea calls this on a
// debounce so typing in the cart doesn't hit the BE per keystroke. The
// endpoint is a tiny PATCH that just stamps ``Order.customer_note`` on
// the caller's draft order and returns the persisted value. ``null``
// clears the previous note.

export interface CartNoteResponse {
  customer_note: string | null;
}

export function useUpdateCartNote(
  options?: Omit<
    UseMutationOptions<CartNoteResponse, Error, string | null>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<CartNoteResponse, Error, string | null>({
    mutationFn: (customerNote) =>
      apiFetch<CartNoteResponse>(`${API}/cart/note`, {
        method: "PATCH",
        body: { customer_note: customerNote },
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // Patch the existing cart cache in place so the next render
      // reflects the saved value without a full refetch. Falling back
      // to invalidation if the cache is empty (first call) keeps
      // semantics simple.
      qc.setQueryData<Cart | undefined>(queryKeys.cart(), (prev) =>
        prev ? { ...prev, customer_note: data.customer_note } : prev,
      );
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

/**
 * KAN-928 — remember the assembly-gate answer on the cart (``PATCH
 * /cart/note`` with ``assembly_intent`` only; the note stays untouched).
 * ``null`` forgets it so the gate asks again.
 */
export function useUpdateCartAssemblyIntent(
  options?: Omit<
    UseMutationOptions<CartNoteResponse, Error, AssemblyIntent | null>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<CartNoteResponse, Error, AssemblyIntent | null>({
    mutationFn: (intent) =>
      apiFetch<CartNoteResponse>(`${API}/cart/note`, {
        method: "PATCH",
        body: { assembly_intent: intent },
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData<Cart | undefined>(queryKeys.cart(), (prev) =>
        prev ? { ...prev, assembly_intent: variables } : prev,
      );
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Objednávkové příplatky (KAN-142) ---------------------------------
//
// Co si zákazník může k zakázce přikoupit a co si vybral. Dnes jediný
// zdroj nabídek je protokol o kontrole, ale FE o tom NEVÍ — vykresluje,
// co přijde. Tenant, který nic nenacenil, vrátí prázdný seznam a v UI
// se nezobrazí nic.

export function useCartAddons(
  options?: Omit<UseQueryOptions<CartAddons>, "queryKey" | "queryFn">,
) {
  return useQuery<CartAddons>({
    queryKey: queryKeys.cartAddons(),
    queryFn: () => apiFetch<CartAddons>(`${API}/cart/addons`),
    staleTime: 5 * 1000,
    ...options,
  });
}

export function useUpdateCartAddons(
  options?: Omit<
    UseMutationOptions<CartAddons, Error, string[]>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<CartAddons, Error, string[]>({
    // Posílá se CELÝ výběr, ne delta — při rychlém klikání nebo ve dvou
    // záložkách by delta skončila výběrem, který uživatel nikdy neviděl.
    mutationFn: (keys) =>
      apiFetch<CartAddons>(`${API}/cart/addons`, {
        method: "PATCH",
        body: { keys },
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // Nejdřív přemaluj zaškrtávátko odpovědí serveru (okamžitá
      // odezva), pak přenačti košík — výběr příplatku mění
      // ``total_czk`` a bez invalidace by souhrn ukazoval starou částku.
      //
      // 🪤 ``queryKeys.cart()`` je ``["cart"]`` a klíč příplatků
      // ``["cart","addons"]``, takže invalidace košíku strhne PREFIXEM
      // i příplatky. Je to záměr (server je zdroj pravdy), ne opomenutí
      // — ``setQueryData`` výš jen překlene čas do doběhnutí refetche.
      qc.setQueryData<CartAddons | undefined>(queryKeys.cartAddons(), data);
      invalidateCart(qc);
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

export function useDeleteCartItem(
  // Mutation variable is the opaque slug — same shape as
  // ``useAddCartLine``'s ``itemSlug``.
  options?: Omit<UseMutationOptions<void, Error, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (itemSlug) => deleteCartItemIdempotent(itemSlug),
    onSuccess: () => {
      invalidateCart(qc);
    },
    ...options,
  });
}

/**
 * KAN-2101 (P18) — smazání položky je idempotentní: 404 znamená, že
 * položka v košíku už není, tedy přesně stav, o který volající žádá.
 * Druhé „Zahodit" v modálu nepotvrzené položky (nebo smazání z jiné
 * záložky) tak už nehlásí „Nepodařilo se to dokončit".
 */
export async function deleteCartItemIdempotent(itemSlug: string): Promise<void> {
  try {
    await apiFetch<void>(`${API}/cart/items/${itemSlug}`, { method: "DELETE" });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return;
    throw error;
  }
}

// --- Sestava / kompletace (KAN-301) ----------------------------------
//
// Náhled je POST, ne GET: vstupem je seznam dětí s násobností, který
// se nevejde rozumně do query stringu, a odpověď se nemá kešovat —
// popisuje návrh, který zákazník právě mění pod rukama.

export function useAssemblyPreview(
  options?: Omit<
    UseMutationOptions<AssemblyPreview, Error, AssemblyChildRequest[]>,
    "mutationFn"
  >,
) {
  return useMutation<AssemblyPreview, Error, AssemblyChildRequest[]>({
    mutationFn: (children) =>
      apiFetch<AssemblyPreview>(`${API}/cart/assembly/preview`, {
        method: "POST",
        body: { children },
      }),
    ...options,
  });
}

export function useComposeAssembly(
  options?: Omit<
    UseMutationOptions<AssemblyComposeResponse, Error, AssemblyChildRequest[]>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AssemblyComposeResponse, Error, AssemblyChildRequest[]>({
    mutationFn: (children) =>
      apiFetch<AssemblyComposeResponse>(`${API}/cart/assembly`, {
        method: "POST",
        body: { children },
      }),
    onSuccess: () => {
      invalidateCart(qc);
    },
    ...options,
  });
}

/**
 * Rozpustí kontejner a vrátí díly mezi kořeny.
 *
 * ⚠️ NENÍ to totéž co ``useDeleteCartItem`` na kontejneru — to by
 * kaskádou smazalo i DĚTI, tedy nakonfigurované díly.
 */
export function useDissolveAssembly(
  options?: Omit<UseMutationOptions<void, Error, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (containerSlug) =>
      apiFetch<void>(`${API}/cart/assembly/${containerSlug}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      invalidateCart(qc);
    },
    ...options,
  });
}

// --- Bulk line update (PR3 košíku redesign) --------------------------
//
// Drives the BulkBar dropdown menus (Materiál ▾ / Tloušťka ▾ / Povrch ▾)
// in apps/web/src/components/cart/BulkBar.tsx. The endpoint accepts a
// patch + line_ids and returns a full cart refresh + per-line failures.

export interface BulkLinePatchRequest {
  material_code?: string | null;
  quantity?: number | null;
  /**
   * Generic per-process dimension patch — merges over each line's
   * stored dimensions (the axis menu sends the value under
   * its canonical dimension key here).
   */
  dimensions?: Record<string, unknown>;
  modifier_code?: string | null;
  /** When true, the BE applies modifier_code (null = remove the existing modifier). */
  apply_finish?: boolean;
}

export interface BulkLineFailure {
  line_id: number;
  error_code: string;
  error_message: string;
}

export interface BulkLineUpdateResponse {
  updated: number;
  failed: BulkLineFailure[];
  cart: Cart;
}


// --- Bulk line duplicate (Duplikovat in BulkBar) ---------------------
//
// Clones each selected line via the BE bulk endpoint. Re-uses the
// per-line ``add_line`` plumbing (auth, capacity, pricing) so the new
// variants are indistinguishable from a manual configurator add.

export interface BulkLineDuplicateResponse {
  created: number;
  failed: BulkLineFailure[];
  cart: Cart;
}

interface BulkDuplicateVars {
  line_ids: number[];
}

export function useBulkDuplicateLines(
  options?: Omit<
    UseMutationOptions<BulkLineDuplicateResponse, Error, BulkDuplicateVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<BulkLineDuplicateResponse, Error, BulkDuplicateVars>({
    mutationFn: ({ line_ids }) =>
      apiFetch<BulkLineDuplicateResponse>(
        `${API}/cart/lines/bulk/duplicate`,
        {
          method: "POST",
          body: { line_ids },
        },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(queryKeys.cart(), data.cart);
      invalidateCart(qc);
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Part configuration (KAN-1652) ------------------------------------
//
// Výběr z konfigurátoru se ukládá na díl (``config_snapshot`` primární
// operace), dokud díl leží v odkladišti. Sdílený odkaz i poptávka obchodu
// ho pak jen odkazují — konfigurace má jeden zdroj.

export interface PartConfigurationPayload {
  material_code?: string | null;
  quantity?: number | null;
  dimensions: Record<string, unknown>;
}

export function useSavePartConfiguration() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { itemSlug: string; payload: PartConfigurationPayload }
  >({
    mutationFn: ({ itemSlug, payload }) =>
      apiFetch<void>(
        `${API}/cart/items/${encodeURIComponent(itemSlug)}/configuration`,
        { method: "PUT", body: payload },
      ),
    // Jen označit za zastaralé: právě otevřený konfigurátor už výběr má,
    // čerstvá data si vezme až příští návštěva stránky.
    onSuccess: (_data, { itemSlug }) => {
      qc.invalidateQueries({
        queryKey: queryKeys.quote(itemSlug),
        refetchType: "none",
      });
      // KAN-2056 — nabídka dalších kroků závisí na materiálu dílu (brána
      // materiálu operace), takže se po změně výběru musí načíst znovu.
      qc.invalidateQueries({ queryKey: chainContinuationKeys.cart(itemSlug) });
    },
  });
}

// --- Save cart as price quote (KAN-1673) -----------------------------
//
// "Uložit jako nabídku" CTA in CartSummaryV2 turns a COPY of the cart into
// a price quote (source = customer): the same model, frozen line prices,
// PDF and locked-price checkout as a seller's quote. The cart itself stays.

export interface SaveCartAsQuoteResponse {
  /** ``CustomQuote`` handle — route to ``/{lang}/quotes/by-id/{public_id}``. */
  public_id: string;
  /** The customer's own reference, ``null`` when left blank. */
  name: string | null;
  /** ISO-8601; validity window from the tenant's ``QuoteSettings``. */
  expires_at: string;
}

export function useSaveCartAsQuote(
  options?: Omit<
    UseMutationOptions<SaveCartAsQuoteResponse, Error, { name?: string }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<SaveCartAsQuoteResponse, Error, { name?: string }>({
    mutationFn: ({ name }) =>
      apiFetch<SaveCartAsQuoteResponse>(`${API}/cart/save-as-quote`, {
        method: "POST",
        body: { name },
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // The quote list in My projects (``/account/quotes``).
      qc.invalidateQueries({ queryKey: queryKeys.myQuotes() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface AddChainOperationVars {
  itemSlug: string;
  payload: { process_type: string; config?: Record<string, unknown> };
}

interface ChainOperationOut {
  id: number;
  process_type: string;
  order_index: number;
  fulfillment_partner_id: number | null;
  config_snapshot: Record<string, unknown> | null;
  unit_price_czk: number;
  estimated_duration_seconds: number;
  // KAN-459: ``estimated_duration_seconds`` cannot tell an authored ``0`` from
  // a step nobody has estimated yet (NULL on the row, KAN-267) — both arrive as
  // a number. This flag is the difference. It is data, not copy: whether the
  // customer sees "bez odhadu" is a product decision that has not been made.
  duration_estimate_missing?: boolean;
}

export function useAddChainOperation(
  options?: Omit<
    UseMutationOptions<ChainOperationOut, Error, AddChainOperationVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<ChainOperationOut, Error, AddChainOperationVars>({
    mutationFn: ({ itemSlug, payload }) =>
      apiFetch<ChainOperationOut>(
        `${API}/cart/items/${itemSlug}/operations`,
        { method: "POST", body: payload },
      ),
    onSuccess: () => {
      invalidateCart(qc);
    },
    ...options,
  });
}

export interface CartInquiryPayload {
  name: string;
  email: string;
  phone?: string | null;
  message?: string | null;
}

/**
 * Turn the current cart into an enquiry instead of an order.
 *
 * For shops whose ``business_info.checkout_mode`` is ``inquiry`` or
 * ``both``. Refused with ``inquiry_mode_disabled`` otherwise — a shop
 * selling by card must not have a quieter second path that lets a
 * customer skip paying.
 */
export function useSubmitCartInquiry() {
  return useMutation<{ contact_message_id: number }, Error, CartInquiryPayload>({
    mutationFn: (payload) =>
      apiFetch<{ contact_message_id: number }>(`${API}/cart/inquiry`, {
        method: "POST",
        body: payload,
      }),
  });
}

/**
 * Third path of the cart's assembly gate (KAN-301/KAN-302): "I don't
 * have it in this shape / want someone to look at it". Converts the
 * *whole* draft cart into an RFQ ``CustomQuote`` instead of proceeding
 * to checkout — owner decision 2026-08-16 rules out a partial checkout
 * of "the rest". The gate dialog (KAN-301) is the intended caller; this
 * hook is the ready-to-wire building block for it.
 */
export function useSubmitCartRfq() {
  return useMutation<CartRfqResponse, Error, CartRfqRequest>({
    mutationFn: (payload) =>
      apiFetch<CartRfqResponse>(`${API}/cart/rfq`, {
        method: "POST",
        body: payload,
      }),
  });
}

/**
 * Split the cart: pay the priced items now, send the rest to manual
 * pricing (KAN-1049, owner's option A1 from 2026-09-07).
 *
 * ⚠️ Refused with ``split_would_break_assembly`` when an unpriced part
 * shares an assembly with a priced one — the owner's 2026-08-16
 * decision ("an assembly is one thing") still stands INSIDE an
 * assembly. Ask ``useCartSplitPreview`` first so the customer is not
 * offered a button that will fail.
 */
export function useSubmitCartSplitRfq() {
  const qc = useQueryClient();
  return useMutation<CartSplitResponse, Error, CartRfqRequest>({
    mutationFn: (payload) =>
      apiFetch<CartSplitResponse>(`${API}/cart/split-rfq`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: () => {
      // Half the cart has just left it. Without this the summary keeps
      // showing the parts that are now on a quote.
      invalidateCart(qc);
    },
  });
}

/**
 * What splitting the cart would do — read-only, changes nothing.
 *
 * Exists because the split is irreversible: the customer has to see
 * whether the paid half drops under the minimum order (KAN-738) BEFORE
 * committing, not after. Also the only honest source for "can this cart
 * be split at all" — the assembly rule lives on the server and a second
 * copy on the FE would drift from it.
 */
export function useCartSplitPreview(
  options?: Omit<UseQueryOptions<CartSplitPreview>, "queryKey" | "queryFn">,
) {
  return useQuery<CartSplitPreview>({
    queryKey: ["cart", "splitPreview"],
    queryFn: () => apiFetch<CartSplitPreview>(`${API}/cart/split-preview`),
    staleTime: 0,
    ...options,
  });
}
